// src/components/resumo/TableInsertModal.tsx
import React, { useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { TableConfig } from './TableManager';

interface TableInsertModalProps {
    darkMode?: boolean;
    onInsert: (config: Partial<TableConfig>) => void;
    onClose: () => void;
}

const headerColors = [
    { label: 'Verde GRAIA', value: '#0D3B0E' },
    { label: 'Verde floresta', value: '#166534' },
    { label: 'Verde médio', value: '#2F7130' },
    { label: 'Verde', value: '#15803d' },
    { label: 'Verde claro', value: '#16a34a' },
    { label: 'Verde menta', value: '#5FBA61' },
    { label: 'Verde vibrante', value: '#22c55e' },
    { label: 'Azul marinho', value: '#0c4a6e' },
    { label: 'Azul prussiano', value: '#2a4a5a' },
    { label: 'Azul escuro', value: '#1e3a8a' },
    { label: 'Azul petróleo', value: '#075985' },
    { label: 'Azul', value: '#2563eb' },
    { label: 'Azul médio', value: '#0369a1' },
    { label: 'Azul claro', value: '#0891b2' },
    { label: 'Azul céu', value: '#0ea5e9' },
    { label: 'Roxo', value: '#7c3aed' },
    { label: 'Vermelho', value: '#dc2626' },
    { label: 'Laranja', value: '#ea580c' },
    { label: 'Cinza escuro', value: '#374151' },
    { label: 'Preto', value: '#111827' },
];

const stripeColors = [
    { label: 'Verde GRAIA suave', value: '#e0ede0' },
    { label: 'Verde muito claro', value: '#f0f7f0' },
    { label: 'Verde menta claro', value: '#dcfce7' },
    { label: 'Verde pastel', value: '#bbf7d0' },
    { label: 'Verde esmeralda suave', value: '#d1fae5' },
    { label: 'Azul prussiano suave', value: '#dce8f0' },
    { label: 'Azul muito claro', value: '#dbeafe' },
    { label: 'Azul céu claro', value: '#e0f2fe' },
    { label: 'Azul pastel', value: '#bae6fd' },
    { label: 'Ciano claro', value: '#cffafe' },
    { label: 'Amarelo claro', value: '#fef9c3' },
    { label: 'Roxo suave', value: '#ede9fe' },
    { label: 'Vermelho suave', value: '#fee2e2' },
    { label: 'Cinza muito claro', value: '#f3f4f6' },
    { label: 'Branco', value: '#ffffff' },
];

const borderColors = [
    { label: 'Verde GRAIA', value: '#0D3B0E' },
    { label: 'Verde floresta', value: '#166534' },
    { label: 'Verde médio', value: '#2F7130' },
    { label: 'Verde', value: '#15803d' },
    { label: 'Verde menta', value: '#5FBA61' },
    { label: 'Verde claro', value: '#4ade80' },
    { label: 'Azul prussiano', value: '#2a4a5a' },
    { label: 'Azul petróleo', value: '#0369a1' },
    { label: 'Azul', value: '#2563eb' },
    { label: 'Azul céu', value: '#0284c7' },
    { label: 'Azul claro', value: '#38bdf8' },
    { label: 'Cinza claro', value: '#e5e7eb' },
    { label: 'Cinza', value: '#d1d5db' },
    { label: 'Cinza médio', value: '#9ca3af' },
    { label: 'Cinza escuro', value: '#374151' },
    { label: 'Preto', value: '#111827' },
];

const TableInsertModal: React.FC<TableInsertModalProps> = ({
    darkMode = false,
    onInsert,
    onClose,
}) => {
    const [rows, setRows] = useState(3);
    const [cols, setCols] = useState(3);
    const [hasHeader, setHasHeader] = useState(true);
    const [zebraStripes, setZebraStripes] = useState(false);
    const [headerBg, setHeaderBg] = useState(() => getComputedStyle(document.body).getPropertyValue('--platform-accent').trim() || '#042563');
    const [stripeBg, setStripeBg] = useState('#f0f7f0');
    const [borderColor, setBorderColor] = useState('#d1d5db');
    const [width, setWidth] = useState(600);

    const ColorSwatches: React.FC<{
        colors: { label: string; value: string }[];
        selected: string;
        onSelect: (v: string) => void;
    }> = ({ colors, selected, onSelect }) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {colors.map(c => (
                <button
                    key={c.value}
                    title={c.label}
                    onClick={() => onSelect(c.value)}
                    style={{
                        width: 24, height: 24,
                        borderRadius: 5,
                        background: c.value,
                        border: selected === c.value
                            ? '3px solid #111'
                            : '2px solid rgba(0,0,0,0.15)',
                        cursor: 'pointer',
                        outline: 'none',
                        transform: selected === c.value ? 'scale(1.2)' : 'scale(1)',
                        transition: 'transform 0.1s',
                        flexShrink: 0,
                    }}
                />
            ))}
        </div>
    );

    const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

    // Preview grade
    const renderPreview = useCallback(() => {
        const previewRows = Math.min(rows, 4);
        const previewCols = Math.min(cols, 5);

        return (
            <div
                className="tbl-grid-preview"
                style={{
                    gridTemplateColumns: `repeat(${previewCols}, 1fr)`,
                    gridTemplateRows: `repeat(${previewRows}, 18px)`,
                }}
            >
                {Array.from({ length: previewRows * previewCols }).map((_, i) => {
                    const rowIdx = Math.floor(i / previewCols);
                    const isHeader = hasHeader && rowIdx === 0;
                    const isZebraRow = zebraStripes && rowIdx % 2 === 0 && !isHeader;
                    return (
                        <div
                            key={i}
                            className={`tbl-preview-cell${isHeader ? ' header-cell' : ''}`}
                            style={{
                                background: isHeader
                                    ? headerBg
                                    : isZebraRow
                                        ? stripeBg
                                        : undefined,
                                borderColor,
                            }}
                        />
                    );
                })}
            </div>
        );
    }, [rows, cols, hasHeader, zebraStripes, headerBg, stripeBg, borderColor]);

    const handleInsert = () => {
        onInsert({
            rows: clamp(rows, 1, 20),
            cols: clamp(cols, 1, 10),
            hasHeader,
            zebraStripes,
            headerBg,
            stripeBg,
            borderColor,
            width: clamp(width, 200, 1200),
        });
    };

    return (
        <div className="tbl-insert-modal-overlay" onClick={onClose}>
            <div
                className={`tbl-insert-modal${darkMode ? ' dark' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="tbl-modal-header">
                    <h3>📊 Inserir Tabela</h3>
                    <button className="tbl-modal-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="tbl-modal-body">
                    {/* Linhas e Colunas */}
                    <div className="tbl-modal-row">
                        <div className="tbl-modal-field">
                            <label>Linhas</label>
                            <input
                                type="number"
                                className="tbl-modal-input"
                                min={1}
                                max={20}
                                value={rows}
                                onChange={(e) => setRows(clamp(parseInt(e.target.value) || 1, 1, 20))}
                            />
                        </div>
                        <div className="tbl-modal-field">
                            <label>Colunas</label>
                            <input
                                type="number"
                                className="tbl-modal-input"
                                min={1}
                                max={10}
                                value={cols}
                                onChange={(e) => setCols(clamp(parseInt(e.target.value) || 1, 1, 10))}
                            />
                        </div>
                        <div className="tbl-modal-field">
                            <label>Largura (px)</label>
                            <input
                                type="number"
                                className="tbl-modal-input"
                                min={200}
                                max={1200}
                                step={50}
                                value={width}
                                onChange={(e) => setWidth(clamp(parseInt(e.target.value) || 600, 200, 1200))}
                            />
                        </div>
                    </div>

                    {/* Opções */}
                    <label className="tbl-modal-toggle">
                        <input
                            type="checkbox"
                            checked={hasHeader}
                            onChange={(e) => setHasHeader(e.target.checked)}
                        />
                        <span>Linha de cabeçalho</span>
                    </label>

                    <label className="tbl-modal-toggle">
                        <input
                            type="checkbox"
                            checked={zebraStripes}
                            onChange={(e) => setZebraStripes(e.target.checked)}
                        />
                        <span>Zebra stripes (linhas alternadas)</span>
                    </label>

                    {/* Cores */}
                    {hasHeader && (
                        <div className="tbl-modal-color-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                            <label>Cor do cabeçalho</label>
                            <ColorSwatches colors={headerColors} selected={headerBg} onSelect={setHeaderBg} />
                        </div>
                    )}

                    {zebraStripes && (
                        <div className="tbl-modal-color-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                            <label>Cor das linhas pares</label>
                            <ColorSwatches colors={stripeColors} selected={stripeBg} onSelect={setStripeBg} />
                        </div>
                    )}

                    <div className="tbl-modal-color-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                        <label>Cor das bordas</label>
                        <ColorSwatches colors={borderColors} selected={borderColor} onSelect={setBorderColor} />
                    </div>

                    {/* Preview */}
                    {renderPreview()}
                </div>

                {/* Actions */}
                <div className="tbl-modal-actions">
                    <button className="tbl-btn-cancel" onClick={onClose}>
                        Cancelar
                    </button>
                    <button className="tbl-btn-insert" onClick={handleInsert}>
                        Inserir Tabela
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TableInsertModal;